import type { AssignableRole, MembershipRole } from "@unbox/api";
import { merchantScopeRoles, workspaceScopeRoles } from "@unbox/api";
import { ChevronDown, ChevronUp } from "@untitledui/icons";
import { useState } from "react";
import { RoleSelect } from "@/components/admin/role-select";
import { WorkspaceLogo } from "@/components/admin/workspace-logo";
import { Badge } from "@/components/base/badges/badges";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { deriveWorkspaceRole, type WorkspaceRoleGrants } from "@/pages/admin/hooks/use-workspace-role-tree";

export type WorkspaceRoleTreeInfo = {
  id: string;
  name: string;
  logoUrl?: string | null;
  merchants: { id: string; name: string; logoUrl?: string | null; region?: string | null }[];
};

type Props = {
  grants: WorkspaceRoleGrants;
  workspacesById: Map<string, WorkspaceRoleTreeInfo>;
  /** Roles the actor may assign (from usePermissions). Workspace-level options. */
  invitableRoles: AssignableRole[];
  /** When omitted, the workspace is fixed (no remove checkbox) — e.g. add-users-to-workspace. */
  onRemoveWorkspace?: (workspaceId: string) => void;
  onToggleBrand: (workspaceId: string, merchantId: string) => void;
  /** `merchantIds` is the workspace's full roster — Merchant Admin cascades across all of it. */
  onSetWorkspaceRole: (workspaceId: string, role: AssignableRole, merchantIds: string[]) => void;
  onSetMerchantRole: (workspaceId: string, merchantId: string, role: MembershipRole) => void;
};

export const WorkspaceRoleTree = ({
  grants,
  workspacesById,
  invitableRoles,
  onRemoveWorkspace,
  onToggleBrand,
  onSetWorkspaceRole,
  onSetMerchantRole,
}: Props) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (workspaceId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(workspaceId)) next.delete(workspaceId);
      else next.add(workspaceId);
      return next;
    });

  // Workspace-level dropdown = summary + bulk setter (WS Admin / Merchant Admin / User;
  // Merchant Admin cascades to every merchant). Merchant-level = the role within that
  // merchant (Merchant Admin / User). Kept in scope-constant order and intersected with
  // what the actor is allowed to assign.
  const workspaceRoleOptions = workspaceScopeRoles.filter((r) => invitableRoles.includes(r));
  const merchantRoleOptions = merchantScopeRoles.filter((r) => invitableRoles.includes(r));

  return (
    <div className="flex flex-col gap-2">
      {Object.entries(grants).map(([wsId, grant]) => {
        const workspace = workspacesById.get(wsId);
        const wsName = workspace?.name ?? wsId;
        const brands = workspace?.merchants ?? [];
        const grantedCount = Object.keys(grant.merchants).length;
        const allSelected = brands.length > 0 && grantedCount === brands.length;
        const isExpanded = expanded.has(wsId);
        const brandCountLabel = allSelected
          ? `All ${brands.length} Merchants`
          : `${grantedCount} of ${brands.length} merchants`;

        const derived = deriveWorkspaceRole(grant);

        return (
          <div key={wsId} className="rounded-lg border border-secondary overflow-hidden">
            <div className="flex items-center gap-3 px-3 py-2.5 bg-tertiary">
              {onRemoveWorkspace && (
                <Checkbox size="md" isSelected onChange={() => onRemoveWorkspace(wsId)} aria-label="Remove workspace" />
              )}
              <WorkspaceLogo name={wsName} logoUrl={workspace?.logoUrl} className="size-7 rounded-md" />
              <span className="text-sm font-medium text-primary min-w-0 truncate">{wsName}</span>
              <span
                className={`text-xs whitespace-nowrap flex-shrink-0 ${isExpanded ? "text-fg-brand-primary" : "text-tertiary"}`}
              >
                {brandCountLabel}
              </span>
              <div className="flex-1" />
              <RoleSelect
                aria-label={`Role for ${wsName}`}
                value={derived}
                options={workspaceRoleOptions}
                onChange={(role) =>
                  onSetWorkspaceRole(
                    wsId,
                    role,
                    brands.map((b) => b.id),
                  )
                }
              />
              {brands.length > 0 && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs font-semibold text-brand-secondary whitespace-nowrap"
                  onClick={() => toggleExpanded(wsId)}
                >
                  Customise
                  {isExpanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
                </button>
              )}
            </div>

            {isExpanded && (
              <div className="border-t border-secondary">
                {brands.length === 0 ? (
                  <p className="text-sm text-tertiary px-3 py-3">No merchants in this workspace yet.</p>
                ) : (
                  <div className="flex flex-col">
                    {brands.map((brand) => {
                      // Workspace Admin ⇒ Merchant Admin on every brand with access to all of
                      // them, so lock each brand checked + role fixed at Merchant Admin, and
                      // disable both so it can't be changed or de-selected (UNB-2001).
                      const brandChecked = grant.workspaceAdmin || brand.id in grant.merchants;
                      return (
                        <div
                          key={brand.id}
                          className={`flex items-center gap-3 px-3 py-2 ${brandChecked ? "bg-tertiary" : ""}`}
                        >
                          <Checkbox
                            size="md"
                            isSelected={brandChecked}
                            isDisabled={grant.workspaceAdmin}
                            onChange={() => onToggleBrand(wsId, brand.id)}
                            aria-label={`Grant access to ${brand.name}`}
                          />
                          {brand.logoUrl ? (
                            <img
                              src={brand.logoUrl}
                              alt={brand.name}
                              className="size-6 rounded object-contain flex-shrink-0"
                            />
                          ) : (
                            <div className="size-6 rounded bg-tertiary flex-shrink-0" />
                          )}
                          <span className="text-sm font-semibold text-primary min-w-0 truncate">{brand.name}</span>
                          {brand.region && (
                            <Badge color="gray" size="sm">
                              {brand.region}
                            </Badge>
                          )}
                          <div className="flex-1" />
                          {brandChecked && (
                            <RoleSelect
                              aria-label={`Role for ${brand.name}`}
                              value={grant.workspaceAdmin ? "merchant_admin" : grant.merchants[brand.id]}
                              options={merchantRoleOptions}
                              isDisabled={grant.workspaceAdmin}
                              onChange={(role) => onSetMerchantRole(wsId, brand.id, role as MembershipRole)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
